import { useEffect, useRef, useState } from 'react';
import { getRiskIndicatorsData, getRegulatoryRiskHits } from '../../store/investor/apiHelpers';
import { getRiskDotColor, resolveEntityQueryKey } from './riskIndicatorUtils';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { downloadFile, getPresignedUrl } from '../Company/UploadJourney/components/UploadCard/utils/api/helpers';
import {
    AICA_GET_TASK_TRACKER_DATA,
    AION_INGEST_AML_PEP_DATA,
    DOWNLOAD_ERROR_MESSAGE,
    RISK_DOT_COLOR_MAP,
    RISK_INDICATOR_STATUS,
    RISK_TYPES,
    TYPES,
    UW_BEFE,
    MAX_POLLING_ATTEMPTS,
    RISK_LABEL_CONFIG,
    RISK_STATE_CONFIG,
    RegulatoryRiskState,
} from './RiskIndicatorConstants';
import { toast } from 'react-toastify';
import { companyBlack, userBlack } from '../../assets/hostedassets';
import styles from './RiskIndicators.module.scss';
import { useAppSelector } from '../../app/hooks';
import { TabModule } from '../../utils/constants/commonConstants';
import type { Row, RegulatoryRiskDrawerState, RegulatoryRiskHitsResult, ViewerState } from './RiskIndicators';
import { useRegulatoryRiskDrawerConfig } from './useRegulatoryRiskDrawerConfig';
import PotentialRiskRedirectIcon from './PotentialRiskRedirectIcon';

export const useRiskIndicatorStates = ({ rows, setRows, autoFetch }) => {
    const modulePollingList = useAppSelector((state) => state.investor.modulePollingList);
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);

    const [loading, setLoading] = useState<boolean>(true);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
    const [pollingAttempts, setPollingAttempts] = useState<number>(0);
    const [regulatoryRiskDrawerState, setRegulatoryRiskDrawerState] = useState<RegulatoryRiskDrawerState | null>(null);
    const [regulatoryRiskHits, setRegulatoryRiskHits] = useState<RegulatoryRiskHitsResult | null>(null);
    const [regulatoryRiskHitsLoading, setRegulatoryRiskHitsLoading] = useState(false);
    const params = useParams();
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const pollingArray = [RISK_INDICATOR_STATUS.PROCESSING, RISK_INDICATOR_STATUS.TODO, RISK_INDICATOR_STATUS.TODO_ASYNC];
    const { config: regulatoryRiskDrawerConfig, ensureConfigLoaded } = useRegulatoryRiskDrawerConfig();

    // Prefetch config on mount so the first drawer open already has title/chip labels
    // resolved, instead of showing raw checkType until ensureConfigLoaded resolves.
    useEffect(() => {
        ensureConfigLoaded(investorOrgId);
    }, [investorOrgId, ensureConfigLoaded]);

    useEffect(() => {
        if (params?.id && autoFetch) {
            fetchRiskIndicators();
        }

        return () => {
            stopPolling();
        };
    }, [params.id, autoFetch]);

    useEffect(() => {
        if (modulePollingList.includes(TabModule.LEGAL_CHECKS)) {
            fetchRiskIndicators();
        }
    }, [modulePollingList]);

    const startPolling = () => {
        if (!pollingRef.current) {
            pollingRef.current = setInterval(() => {
                setPollingAttempts((prevAttempts) => {
                    const newAttempts = prevAttempts + 1;

                    if (newAttempts < MAX_POLLING_ATTEMPTS) {
                        fetchRiskIndicators();
                        return newAttempts;
                    } else {
                        stopPolling();
                        return newAttempts;
                    }
                });
            }, 10000);
        }
    };

    const stopPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
        setPollingAttempts(0);
        setLoading(false);
    };

    const fetchRiskIndicators = async () => {
        const payload = {
            orgId: params?.id ?? '',
        };
        setLoading(true);
        let response = await getRiskIndicatorsData(payload);

        if (response) {
            handleTaskTrackerData(response?.responseBody?.[AICA_GET_TASK_TRACKER_DATA]);
            processUwBefeData(response?.responseBody?.[UW_BEFE]);
        }
        if (!pollingRef.current) {
            setLoading(false);
        }
    };

    const handleTaskTrackerData = (taskTrackerData) => {
        if (taskTrackerData) {
            const pollingStatus = taskTrackerData?.taskTrackerData?.[AION_INGEST_AML_PEP_DATA];
            if (!pollingStatus || pollingArray.includes(pollingStatus)) {
                startPolling();
            } else {
                stopPolling();
            }
        }
    };

    const processUwBefeData = (uwBefeData) => {
        if (uwBefeData && Object.keys(uwBefeData).length > 0) {
            const formattedLastUpdatedAt = uwBefeData.lastUpdatedAt ? dayjs(uwBefeData.lastUpdatedAt).format('DD MMM YYYY') : null;

            setRows(uwBefeData?.data || []);
            setLastUpdatedAt(formattedLastUpdatedAt || null);
        } else {
            setRows([]);
            setLastUpdatedAt(null);
        }
    };

    const getStatusMessage = () => {
        if (loading) {
            return 'Processing (may take ~5 mins)';
        } else if (rows.length === 0) {
            return 'No Data Available';
        } else if (lastUpdatedAt) {
            return `Last Updated on ${lastUpdatedAt}`;
        }
        return '';
    };






    // Stale-response guard (mirrors useCommentsPagination's requestTokenRef): a fast
    // pill A -> B switch can let A's late response land after B's request started.
    // Only commit the result if this request is still the most recent one opened.
    const regulatoryRiskRequestTokenRef = useRef(0);

    const onOpenRegulatoryRisk = async (row: Row, checkType: string) => {
        const token = ++regulatoryRiskRequestTokenRef.current;
        setRegulatoryRiskDrawerState({ row, checkType });
        setRegulatoryRiskHits(null);
        setRegulatoryRiskHitsLoading(true);
        // getRegulatoryRiskHits never rejects (it catches internally and resolves null),
        // so this single post-await branch covers both the success and error cases.
        // resolveEntityQueryKey handles the null-identifier sentinel fallback — see its doc.
        const result = await getRegulatoryRiskHits({
            orgId: params?.id ?? '',
            ...resolveEntityQueryKey(row),
            checkType,
        });
        if (token === regulatoryRiskRequestTokenRef.current) {
            setRegulatoryRiskHits(result);
            setRegulatoryRiskHitsLoading(false);
        }
    };

    const closeRegulatoryRiskDrawer = () => {
        setRegulatoryRiskDrawerState(null);
        setRegulatoryRiskHits(null);
        setRegulatoryRiskHitsLoading(false);
    };

    const getRowImage = (type) => {
        switch (type) {
            case TYPES.ORGANIZATION:
                return <img src={companyBlack} alt="company" style={{ width: '16px', height: '16px', marginTop: '2px' }} />;
            case TYPES.INDIVIDUAL:
                return <img src={userBlack} alt="user" style={{ width: '16px', height: '16px', marginTop: '2px' }} />;
            default:
                return null;
        }
    };

    const RiskDotMessage = () => {
        return (
            <div className={styles.RiskDotMessage}>
                {RISK_TYPES.map((risk, index) => (
                    <div key={index} className={styles.RiskIndicators}>
                        <span className={styles.RiskDot} style={{ backgroundColor: risk.color }}></span>
                        <span>{risk.label}</span>
                    </div>
                ))}
            </div>
        );
    };

    /*
        RECUR-27829. Two renderers, deliberately kept apart:

        - getStateIndicator reads the per-cell state the BE derives from the actual hit rows.
          This is the path from now on.
        - getScoreIndicator is the OLD score path, kept verbatim as the rollout fallback.
          polus omits riskState until underwriting-engine ships, and this repo may deploy
          first, so the column must not go blank in that window.

        Openability is keyed on the STATE, not on the label config. It used to be set only in
        the POTENTIAL_RISK branch, so mapping the new CONFIRMED state onto the existing
        HIGH_RISK label would have made the most severe cell the only unclickable one.
    */
    /*
        `pill` distinguishes the two callers, which want different boxes.

        The legacy score path draws a real pill: the cell behind it is untinted, so the label needs
        its own background, padding and radius to read as a chip.

        The RECUR-27829 state path does not. The design tints the whole CELL and puts a plain
        icon+text row inside it (Figma 34293-27364: "Table cell" carries the fill and 16px of
        padding; there is no inner chip). Giving that row its own 10px of padding pushed every
        value 10px right of its own column header — measured: header text at x=848, cell content
        at x=858 — so the column read as misaligned all the way down.
    */
    const renderIndicator = ({ label, color, backgroundColor, openable, row, checkType, onOpen, pill = true }) => {
        const openDrawer = () => row && checkType && onOpen?.(row, checkType);
        return (
            <span
                role={openable ? 'button' : undefined}
                tabIndex={openable ? 0 : undefined}
                onClick={openable ? openDrawer : undefined}
                onKeyDown={
                    openable
                        ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openDrawer();
                              }
                          }
                        : undefined
                }
                style={{
                    // On the state path the cell owns the tint and the padding; see `pill` above.
                    ...(pill ? { backgroundColor, padding: '5px 10px', borderRadius: '16px' } : null),
                    color,
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: openable ? 'pointer' : 'default',
                    /*
                        The design lays the cell contents out as a row with the icon FIRST and a
                        6px gap (Figma 34293-27364: Container > Icon, then Text). Inline-flex
                        rather than plain inline so the gap is real spacing instead of the icon
                        carrying its own margin, and so the two never wrap apart from each other.
                    */
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    whiteSpace: 'nowrap',
                }}
            >
                {openable && <PotentialRiskRedirectIcon className={styles.RiskRedirectIcon} color={color} />}
                {label}
            </span>
        );
    };

    const getScoreIndicator = (risk, row?: Row, checkType?: string, onOpen?: (row: Row, checkType: string) => void) => {
        let config;
        let isPotentialRisk = false;
        switch (true) {
            case risk === 0:
                config = RISK_LABEL_CONFIG.NO_RISK;
                break;
            case risk > 0 && risk <= 100:
                config = RISK_LABEL_CONFIG.POTENTIAL_RISK;
                isPotentialRisk = true;
                break;
            default:
                config = RISK_LABEL_CONFIG.NOT_AVAILABLE;
        }
        return renderIndicator({
            label: config.label,
            color: config.color,
            backgroundColor: config.backgroundColor,
            openable: isPotentialRisk,
            row,
            checkType,
            onOpen,
        });
    };

    const getRiskIndicator = (risk, row?: Row, checkType?: string, onOpen?: (row: Row, checkType: string) => void) => {
        const cell = checkType ? row?.riskState?.[checkType] : undefined;
        if (!cell?.state) return getScoreIndicator(risk, row, checkType, onOpen);

        const config = RISK_STATE_CONFIG[cell.state] ?? RISK_STATE_CONFIG[RegulatoryRiskState.NOT_AVAILABLE];
        // Per the ticket: show the confirmed count when there is one, otherwise the probable
        // count. A state that carries no count (No Match / No Data) renders its label alone.
        const count = (cell.confirmedCount ?? 0) > 0 ? cell.confirmedCount : cell.probableCount;
        const label = config.word ? `${count} ${config.word}` : config.label;

        return renderIndicator({
            label,
            color: config.color,
            backgroundColor: config.backgroundColor,
            openable: config.openable,
            row,
            checkType,
            onOpen,
            pill: false,
        });
    };


    const getRiskDot = (risk, state?: string) => {
        const color = getRiskDotColor(risk, state);
        return (
            <span
                style={{
                    backgroundColor: color,
                }}
                className={styles.RiskDot}
            ></span>
        );
    };

    return {
        loading,
        hoveredIndex,
        setHoveredIndex,
        lastUpdatedAt,
        getStatusMessage,
        getRowImage,
        RiskDotMessage,
        getRiskIndicator,
        getRiskDot,
        regulatoryRiskDrawerState,
        regulatoryRiskHits,
        regulatoryRiskHitsLoading,
        onOpenRegulatoryRisk,
        closeRegulatoryRiskDrawer,
        regulatoryRiskDrawerConfig,
    };
};
